import { ReactElement } from "react";
import { createPortal } from "react-dom";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";

import styles from "./LeaveFlowsheetConfirmationModal.module.scss";

/**
 * The properties for a {@link LeaveFlowsheetConfirmationModal} component.
 */
interface ILeaveFlowsheetConfirmationModalProps {
    /**
     * A callback invoked when leaving the flowsheet is confirmed.
     */
    onConfirm: () => void;

    /**
     * A callback invoked when leaving the flowsheet is cancelled.
     */
    onCancel?: () => void;
}

/**
 * A modal that asks to confirm when a leaving a flowsheet.
 * @param props The LeaveFlowsheetConfirmationModal properties.
 */
function LeaveFlowsheetConfirmationModal({ onConfirm, onCancel }: Readonly<ILeaveFlowsheetConfirmationModalProps>): ReactElement {
    const { getText } = useTranslation();

    return (
        // Portal is required to prevent the parent from scrolling when focusing something in the modal
        createPortal(
            <Modal
                title={getText("Flowsheets_WarningLabel")}
                showX
                disableAutoScroll
                footer={(
                    <div className={styles.leaveFlowsheetConfirmationModalFooter}>
                        <ButtonGroup spacing="sm">
                            <ButtonControl
                                variant="neutral"
                                onClick={() => onCancel?.()}
                                label={cacheStore.GetString("Flowsheets_GoBackLabel")}
                            />
                            <ButtonControl
                                variant="brand"
                                onClick={() => onConfirm()}
                                label={cacheStore.GetString("OKLabel")}
                            />
                        </ButtonGroup>
                    </div>
                )}
                onClose={() => onCancel?.()}
            >
                <div className={styles.messageContainer}>
                    <p>{getText("Flowsheets_ConfirmLeave_EditsWillBeLostMessage")}</p>
                    <p>{getText("Flowsheets_ConfirmLeave_AreYouSureMessage")}</p>
                </div>
            </Modal>,
            document.body
        )
    );
}

export { LeaveFlowsheetConfirmationModal };
